import { useAuth } from '@agentnet/auth';
import { FullStory, init as initFullStory, isInitialized as isFullStoryInitialized } from '@fullstory/browser';
import { getStateOptions } from 'api/profile/profile-utilities';
import { useAnonymousTracking } from 'hooks/AnonymousTrackingContext';
import { useContext, useEffect, useMemo } from 'react';
import { ProductFruits } from 'react-product-fruits';
import { RoleNameValue } from '../../../api/file/constants';
import { ProfileContext, ProfileContextInterface } from '../../../hooks/ProfileContext';
import { useFullStoryGuidesIdentity } from '../../../hooks/useFullStoryGuidesIdentity';
import {
  buildAnonymousFullStoryUser,
  buildAnonymousProductFruitsUser,
} from '../../../utilities/fullstory/anonymousUser';

const ProductFruitsFullStory = (): JSX.Element => {
  const { account, isAuthenticated } = useAuth();
  const { firmName } = useAnonymousTracking();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, profile } = profileCtx;
  /* Memoized so the identity objects below keep a stable reference and setIdentity is not re-sent on every render */
  const stateOptions = useMemo(() => (userFirm?.offices ? getStateOptions(userFirm) : []), [userFirm]);
  const isUserAuthenticated = isAuthenticated();

  const pfUserInfo = useMemo(() => {
    if (!isUserAuthenticated) {
      return buildAnonymousProductFruitsUser(firmName);
    }

    if (!profile || !profile.login) {
      return null;
    }

    return {
      username: profile?.login ?? '',
      email: profile?.emailAddress ?? '',
      firstname: profile?.firstName ?? '',
      lastname: profile?.lastName ?? '',
      signUpAt: '',
      role: RoleNameValue[profile?.roleId ?? 0],
      props: {
        idaasGuid: account?.localAccountId ?? '',
        firmName: userFirm?.name ?? '',
        states: stateOptions.length == 0 ? '' : Array.prototype.map.call(stateOptions, (s) => s.value).toString(),
        policySoftware: userFirm?.policySoftware ?? '',
        canRemit:
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 33) &&
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 33).length > 0
            ? true
            : false,
        canPay:
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 61) &&
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 61).length > 0
            ? true
            : false,
        isAgentNetAdmin:
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 36) &&
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 36).length > 0
            ? true
            : false,
        lastLoginDateTime: profile?.lastLoginDateTime ?? '',
        fraudValidation:
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 141) &&
          profile?.activityRights?.filter((ar) => ar.ActivityRightId === 141).length > 0
            ? true
            : false,
      },
    };
  }, [account?.localAccountId, firmName, isUserAuthenticated, profile, stateOptions, userFirm]);

  const fSUserInfo = useMemo(() => {
    if (!isUserAuthenticated) {
      return buildAnonymousFullStoryUser(firmName);
    }

    if (!profile || !profile.login) {
      return null;
    }

    const activityRights = profile?.activityRights ?? [];

    return {
      uid: profile?.login ?? '',
      properties: {
        email: profile?.emailAddress ?? '',
        firstName: profile?.firstName ?? '',
        lastName: profile?.lastName ?? '',
        role: RoleNameValue[profile?.roleId ?? 0],
        idaasGuid: account?.localAccountId ?? '',
        firmName: userFirm?.name ?? '',
        states: stateOptions.length == 0 ? '' : Array.prototype.map.call(stateOptions, (s) => s.value).toString(),
        policySoftware: userFirm?.policySoftware ?? '',
        canRemit: activityRights.some((ar) => ar.ActivityRightId === 33),
        canPay: activityRights.some((ar) => ar.ActivityRightId === 61),
        isAgentNetAdmin: activityRights.some((ar) => ar.ActivityRightId === 36),
        fraudValidation: activityRights.some((ar) => ar.ActivityRightId === 141),
      },
    };
  }, [account?.localAccountId, firmName, isUserAuthenticated, profile, stateOptions, userFirm]);

  const fSGuideUserInfo = useMemo(
    () =>
      fSUserInfo
        ? {
            uid: fSUserInfo.uid,
            properties: Object.fromEntries(
              Object.entries(fSUserInfo.properties).map(([key, value]) => [key, String(value)]),
            ),
          }
        : null,
    [fSUserInfo],
  );

  useEffect(() => {
    if (!fSUserInfo) {
      return;
    }

    if (!isFullStoryInitialized()) {
      initFullStory({ orgId: 'o-1VHTDK-na1' });
    }

    FullStory('setIdentity', fSUserInfo);
  }, [fSUserInfo]);

  useFullStoryGuidesIdentity(fSGuideUserInfo);

  return <>{pfUserInfo && <ProductFruits workspaceCode="t2D9aOCQfJddTekV" language="en" user={pfUserInfo} />}</>;
};

export default ProductFruitsFullStory;
